import {
  batteryContractPath,
  batterySetupGroupPath,
  batterySetupStepPath,
  batterySharePath,
  SETUP_GROUPS,
  type BatterySetupGroupId,
  type BatterySetupRouteSuffix,
} from "./setup-steps"
import { PRICING_REQUIRED_HINT } from "../../ProposalSetup/pricing/pricing-copy"
import { matchProposalSetupSearch } from "../../ProposalSetup/proposal-setup-search"
import type { BatteryDrawerLaunchState } from "../financing-status/battery-drawer-launch"

export const INCENTIVES_LOCKED_HINT = PRICING_REQUIRED_HINT

export type BatterySetupSearchCommand =
  | {
      readonly kind: `export`
    }
  | {
      readonly kind: `locked`
      readonly hint: string
    }
  | {
      readonly kind: `navigate`
      readonly to: string
      readonly state?: BatteryDrawerLaunchState
    }

export type BatterySetupSearchEntry = {
  readonly id: string
  readonly label: string
  readonly groupLabel: string
  readonly command: BatterySetupSearchCommand
}

const ROUTE_PART_PATH: Record<
  BatterySetupRouteSuffix,
  (proposalId: string) => string
> = {
  contract: batteryContractPath,
  share: batterySharePath,
}

export const batterySetupSearchEntries = ({
  hasPricing,
  proposalId,
  savingsEnabled,
}: {
  readonly hasPricing: boolean
  readonly proposalId: string
  readonly savingsEnabled: boolean
}): readonly BatterySetupSearchEntry[] => {
  const entries: BatterySetupSearchEntry[] = []

  for (const group of SETUP_GROUPS) {
    entries.push({
      id: `group:${group.id}`,
      label: group.label,
      groupLabel: group.label,
      command: {
        kind: `navigate`,
        to: batterySetupGroupPath(proposalId, group.id),
      },
    })

    for (const part of group.parts) {
      if (part.id === `arbitrage` && !savingsEnabled) {
        continue
      }

      entries.push(
        entryForPart(part, group.id, group.label, proposalId, hasPricing),
      )
    }
  }

  return entries
}

const entryForPart = (
  part: (typeof SETUP_GROUPS)[number][`parts`][number],
  groupId: BatterySetupGroupId,
  groupLabel: string,
  proposalId: string,
  hasPricing: boolean,
): BatterySetupSearchEntry => {
  const base = { id: part.id, label: part.label, groupLabel }

  switch (part.kind) {
    case `step`:
      if (part.requiresPricing && !hasPricing) {
        return {
          ...base,
          command: { kind: `locked`, hint: INCENTIVES_LOCKED_HINT },
        }
      }
      return {
        ...base,
        command: {
          kind: `navigate`,
          to: batterySetupStepPath(proposalId, part.id),
        },
      }
    case `route`:
      return {
        ...base,
        command: {
          kind: `navigate`,
          to: ROUTE_PART_PATH[part.pathSuffix](proposalId),
          state: { returnTo: batterySetupGroupPath(proposalId, groupId) },
        },
      }
    case `action`: {
      const actionId = part.id
      switch (actionId) {
        case `export-pdf`:
          return { ...base, command: { kind: `export` } }
        default: {
          const _exhaustive: never = actionId
          return _exhaustive
        }
      }
    }
    default: {
      const _exhaustive: never = part
      return _exhaustive
    }
  }
}

export const matchBatterySetupSearch = (
  query: string,
  entries: readonly BatterySetupSearchEntry[],
): readonly BatterySetupSearchEntry[] => matchProposalSetupSearch(query, entries)
